import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';

import lodash from 'lodash';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { TimeService } from '@bayclubs/utils';
import { TranslateService } from '@ngx-translate/core';

import { GroupClassesSecurityService } from '../../../../infrastructure/infrastructure.module';
import { DataFilteringService, FiltersStorageService } from '../../../../shared/shared.module';

import {
    CalendarMode,
    CalendarModel,
    CategoryModel,
    ClubModel,
    ContextModel,
    CopyGroupClassesScheduleMethod,
    LocationModel,
} from '@bayclubs/group-classes-employee-api';
import { CalendarEventsService } from '../calendar-events.service';

@Component({
    selector: 'app-calendar-settings',
    templateUrl: './calendar-settings.component.html',
    styleUrls: ['./calendar-settings.component.scss'],
    standalone: false
})
export class CalendarSettingsComponent implements OnInit, OnDestroy {
    @Input()
    public searchForm: UntypedFormGroup;

    @Input()
    public model: CalendarModel;

    public advancedFilters = false;

    public calendarModes: Array<any> = [];

    public copyWeekOptions: Array<any> = [];
    public copyDayOptions: Array<any> = [];

    public clubs: ClubModel[];
    public categories: CategoryModel[];
    public locations: LocationModel[];

    private subscriptions: Subscription[] = [];

    public locationGroupDisplayNameFunc: (group: string) => string = group => (group == 'Court' ? 'Courts' : 'Group classes');

    public context: ContextModel = {
        clubs: [],
        locations: [],
        categories: [],
        statuses: [],
        bookingWindowUnits: [],
        instructors: [],
        classTypes: [],
        streamingServices: [],
        clubGroupClassCategories: [],
        adpPayPeriods: [],
        revenueDepartments: [],
    };

    constructor(
        private translate: TranslateService,
        private time: TimeService,
        private eventsService: CalendarEventsService,
        private activatedRoute: ActivatedRoute,
        private security: GroupClassesSecurityService,
        private filteringService: DataFilteringService,
        private router: Router,
        private filtersStorageService: FiltersStorageService
    ) {
        this.context = lodash.cloneDeep(activatedRoute.snapshot.data.context);
    }

    ngOnInit() {
        this.subscribeToQueryParamChanges();
        this.initCalendarModes();

        this.initClubs();
        this.initCopyWeekOptions();
        this.initCopyDayOptions();
        this.subscriptions.push(
            this.searchForm.controls['date'].valueChanges
                .pipe(distinctUntilChanged((prev, curr) => prev.value === curr.value))
                .subscribe(value => {
                    this.initCopyWeekOptions();
                    this.initCopyDayOptions();
                })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(i => i.unsubscribe());
    }

    private initClubs() {
        this.security.filterClubsForViewClasses(this.context.clubs).subscribe(filtered => {
            this.clubs = filtered;
        });
    }

    private assignAvailableCategoriesByClubs(clubIds: string[] = null): void {
        this.categories = this.filteringService.getAvailableCategoriesByClubs<CategoryModel, string>(clubIds, this.context);
    }

    private assignAvailableLocationsByClubs(clubId: string = null): void {
        this.locations = this.model.locations.filter(x => !clubId || clubId == x.clubId);
    }

    private subscribeToQueryParamChanges() {
        this.activatedRoute.queryParamMap.subscribe(params => {
            const calendarModeParam: string = params.get('calendarMode');
            if (calendarModeParam != null) {
                const calendarControl = this.searchForm.controls['calendarMode'];
                if (calendarModeParam) {
                    calendarControl.setValue(calendarModeParam, { emitEvent: false });
                }
            }

            const clubIdParam: string = params.get('clubId');
            if (clubIdParam) {
                const clubIdControl = this.searchForm.controls['clubId'];
                clubIdControl.setValue(clubIdParam.toLowerCase(), { emitEvent: false });
                const clubIdArray = clubIdParam == null ? null : [clubIdParam];
                this.assignAvailableCategoriesByClubs(clubIdArray);
                this.assignAvailableLocationsByClubs(clubIdParam);
            } else {
                this.assignAvailableCategoriesByClubs(null);
                this.assignAvailableLocationsByClubs(null);
            }

            const categoriesCodes: string[] = params.getAll('categories');
            const categories: CategoryModel[] = this.context.categories.filter(c => lodash.includes(categoriesCodes, c.code));
            const categoriesControl = this.searchForm.controls['categories'];
            categoriesControl.setValue(categories || [], { emitEvent: false });

            const locationIds: string[] = params.getAll('locations');
            const locations: any[] = this.locations.filter(x => lodash.includes(locationIds, x.id));
            const locationsControl = this.searchForm.controls['locationIds'];
            locationsControl.setValue(locations || [], { emitEvent: false });

            const dateParam: string = params.get('date');
            if (dateParam) {
                const dateControl = this.searchForm.controls['date'];
                const dateObj = { value: dateParam };
                dateControl.setValue(dateObj, { emitEvent: false });
            }

            const substitutedOnlyParam: string = params.get('substitutedOnly');
            const substitutedOnlyValue = substitutedOnlyParam === 'true';
            const substitutedOnlyControl = this.searchForm.controls['substitutedOnly'];
            substitutedOnlyControl.setValue(substitutedOnlyValue, { emitEvent: false });

            const advancedFiltersParam: string = params.get('filters');
            const advancedFilters: boolean = advancedFiltersParam === '1';
            this.advancedFilters = advancedFilters;
        });
    }

    private initCalendarModes(): void {
        this.calendarModes.push({ name: 'Day', code: CalendarMode.Day });
        this.calendarModes.push({ name: 'Week', code: CalendarMode.Week });
    }

    private initCopyWeekOptions(): void {
        this.copyWeekOptions = [];

        this.copyWeekOptions.push({
            value: CopyGroupClassesScheduleMethod.CopyWeekToNextWeek,
            name: this.translate.instant('CopyGroupClassesScheduleMethod.CopyWeekToNextWeek'),
        });

        if (this.shouldShowCopyWeekToAllWeeksThisMonth()) {
            this.copyWeekOptions.push({
                value: CopyGroupClassesScheduleMethod.CopyWeekToAllWeeksThisMonth,
                name: this.translate.instant('CopyGroupClassesScheduleMethod.CopyWeekToAllWeeksThisMonth'),
            });
        }
    }

    private shouldShowCopyWeekToAllWeeksThisMonth(): boolean {
        const currentDateAsMoment = this.time.toMoment(this.currentDate);

        let startOfWeek = this.time.startOfWeek(currentDateAsMoment);
        const endOfWeek = this.time.endOfWeek(currentDateAsMoment);

        while (startOfWeek <= endOfWeek) {
            if (this.time.isLastDayOfMonth(startOfWeek)) {
                return false;
            }

            startOfWeek = this.time.addDays(startOfWeek, 1);
        }

        return true;
    }

    private initCopyDayOptions(): void {
        this.copyDayOptions = [];

        this.copyDayOptions.push({
            value: CopyGroupClassesScheduleMethod.CopyDayToTomorrow,
            name: 'CopyGroupClassesScheduleMethod.CopyDayToTomorrow',
        });

        this.copyDayOptions.push({
            value: CopyGroupClassesScheduleMethod.CopyDayToNextWeek,
            name: 'CopyGroupClassesScheduleMethod.CopyDayToNextWeek',
        });

        if (this.shouldShowCopyDayToEveryWeekdayThisMonth()) {
            this.copyDayOptions.push({
                value: CopyGroupClassesScheduleMethod.CopyDayToEveryWeekdayThisMonth,
                name: 'CopyGroupClassesScheduleMethod.CopyDayToEveryWeekdayThisMonth',
            });
        }
    }

    private shouldShowCopyDayToEveryWeekdayThisMonth(): boolean {
        const currentDateAsMoment = this.time.toMoment(this.currentDate);
        const sameDayInNextWeek = this.time.addDays(currentDateAsMoment, 7);

        return currentDateAsMoment.isSame(sameDayInNextWeek, 'month');
    }

    public onCalendarModeChanged(newMode: CalendarMode) {
        if (newMode == this.calendarMode) {
            return;
        }
    }

    public onNext() {
        if (this.isDay) {
            this.addDays(1);
        }
        if (this.isWeek) {
            this.addDays(7);
        }
    }

    public onPrevious() {
        if (this.isDay) {
            this.addDays(-1);
        }
        if (this.isWeek) {
            this.addDays(-7);
        }
    }

    public onCopySchedule(option: CopyGroupClassesScheduleMethod): void {
        this.eventsService.onCopySchedule(option);
    }

    private addDays(days: number) {
        var momentDate = this.time.toMoment(this.currentDate);
        var momentDate = this.time.addDays(momentDate, days);
        this.searchForm.controls['date'].setValue(this.time.toBcDate(momentDate));
    }

    get calendarMode() {
        return this.searchForm.controls['calendarMode'].value;
    }

    get isWeek() {
        return this.calendarMode == CalendarMode.Week;
    }

    get isDay() {
        return this.calendarMode == CalendarMode.Day;
    }

    get currentDate() {
        return this.searchForm.controls['date'].value;
    }

    public showClubsDropdown() {
        return this.clubs.length > 1;
    }

    public isLocationsDropdownEnabled() {
        return (
            this.calendarMode == CalendarMode.Week &&
            this.model != null &&
            this.model.locations != null &&
            this.model.locations.length > 0
        );
    }

    public onlySubstitutedChanged(substitutedOnly: boolean) {
        this.searchForm.get('substitutedOnly').setValue(substitutedOnly);
    }

    public changeTypeOfFilters(): void {
        this.advancedFilters = !this.advancedFilters;

        const params = { filters: this.advancedFilters ? '1' : '0' };
        this.mergeQueryParam(params);
    }

    private mergeQueryParam(params: Params): void {
        this.router
            .navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: params,
                queryParamsHandling: 'merge',
            })
            .then(() => (this.filtersStorageService.calendarUrl = this.router.url));
    }
}
